/**
 * A aba PESSOAS da Equipe (H129, spec 24): a lista com filtros (time, presença, jornada) e carga, e o REGISTRO de
 * cada pessoa na gaveta — times, perfil, jornada, desempenho de 30 dias e as ações.
 *
 * `?pessoa=<id>` abre o registro dela: é por onde o "Equipe agora" do Início chega (pôr em fila continua sendo aqui,
 * decisão do owner em 01/11).
 */

import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Botao, Retrato, useSessao } from "@hugi/ui";
import { Dica, ListView, Selo, type ColunaDaLista, type VistaDaLista } from "@hugi/ui/kit";
import { Users } from "lucide-react";
import type { FalhaDaApi } from "@hugi/ui";
import {
  desvincularDaFila,
  estadoDaJornada,
  operarEquipe,
  trocarPerfilDeAcesso,
  useCatalogos,
  vincularAFila,
  type AtendenteNaLista,
  type Equipe as DadosDaEquipe,
  type TimeNaLista,
} from "./dados.js";
import { Carga, FormDePresenca, MenuDoSupervisor, PontoNaTela, SeloDePresenca, duracaoCurta } from "./equipe-comum.js";

const DIAS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"] as const;

const JORNADA: Readonly<Record<string, string>> = {
  "em jornada": "Em jornada",
  liberado: "Liberado hoje",
  fora: "Fora da jornada",
  inativo: "Inativo",
};

/** Quem a pessoa que olha supervisiona: administra a equipe, ou é responsável por um time em que a outra está. */
export function supervisionaQuem(sessaoId: string, administra: boolean, times: readonly TimeNaLista[]): (id: string) => boolean {
  if (administra) return () => true;
  const meus = times.filter((t) => t.membros.some((m) => m.responsavel && m.atendenteId === sessaoId));
  const ids = new Set(meus.flatMap((t) => t.membros.map((m) => m.atendenteId)));
  return (id) => ids.has(id);
}

export function AbaPessoas(props: {
  readonly dados: DadosDaEquipe;
  readonly times: readonly TimeNaLista[];
  readonly pedida: string | null;
  readonly aoAbrir: (id: string | null) => void;
}) {
  const d = props.dados;
  const sessao = useSessao();
  const [time, setTime] = useState("");
  const administra = sessao.permissoes.includes("manage_team");
  const supervisiona = supervisionaQuem(sessao.usuario.id, administra, props.times);
  const motivos = d.motivosDePausa ?? [];
  const linhas = time ? d.itens.filter((a) => a.filas.some((f) => f.id === time)) : d.itens;
  const jornadaDe = (a: AtendenteNaLista) => estadoDaJornada(a, d.horarioDoTenant, d.fuso);
  const filasDoFiltro = [...new Map(d.itens.flatMap((a) => a.filas).map((f) => [f.id, f.nome])).entries()];

  const vistas: VistaDaLista<AtendenteNaLista>[] = [
    { id: "todos", rotulo: "Todos", passa: () => true },
    { id: "disponiveis", rotulo: "Disponíveis", passa: (a) => a.disponibilidade?.estado === "disponivel" },
    { id: "pausa", rotulo: "Em pausa", passa: (a) => a.disponibilidade?.estado === "pausa" },
    { id: "offline", rotulo: "Offline", passa: (a) => a.disponibilidade?.estado === "offline" },
    { id: "fora", rotulo: "Fora da jornada", passa: (a) => jornadaDe(a) === "fora" },
    { id: "inativos", rotulo: "Inativos", passa: (a) => !a.ativo },
  ];

  const colunas: ColunaDaLista<AtendenteNaLista>[] = [
    {
      id: "nome",
      rotulo: "Pessoa",
      principal: true,
      link: true,
      ordenar: (a) => a.nome ?? "",
      celula: (a) => (
        <span className="hugi-equipe-celula-pessoa">
          <span className="hugi-time-avatar">
            <Retrato nome={a.nome ?? "?"} />
            <PontoNaTela naTela={a.naTela} />
          </span>
          {a.nome ?? "Sem nome ainda"}
          {a.atendenteId === sessao.usuario.id ? <Selo tom="neutro">você</Selo> : null}
        </span>
      ),
    },
    {
      id: "times",
      rotulo: "Times",
      celula: (a) =>
        a.filas.length === 0 ? (
          <span className="hugi-equipe-atencao">sem time</span>
        ) : (
          a.filas.map((f) => `${f.nome}${f.responsavel ? " (resp.)" : ""}`).join(", ")
        ),
      dica: (a) => a.filas.map((f) => f.nome).join(", "),
    },
    { id: "presenca", rotulo: "Presença", celula: (a) => <SeloDePresenca disponibilidade={a.disponibilidade} /> },
    {
      id: "carga",
      rotulo: "Abertas",
      numerica: true,
      ordenar: (a) => a.atendimentosAbertos,
      celula: (a) => <Carga abertos={a.atendimentosAbertos} limite={a.limiteSimultaneo} />,
    },
    { id: "jornada", rotulo: "Jornada", celula: (a) => JORNADA[jornadaDe(a)] ?? "—" },
    {
      id: "acoes",
      rotulo: "",
      acoes: true,
      celula: (a) =>
        a.ativo && a.atendenteId !== sessao.usuario.id && supervisiona(a.atendenteId) ? (
          <MenuDoSupervisor
            alvo={{ atendenteId: a.atendenteId, nome: a.nome, abertos: a.atendimentosAbertos, ...(a.disponibilidade ? { disponibilidade: a.disponibilidade } : {}) }}
            podeRedistribuir={sessao.permissoes.includes("reassign_conversation")}
            destinos={d.itens.filter((x) => x.ativo).map((x) => ({ id: x.atendenteId, nome: x.nome, ...(x.disponibilidade ? { disponibilidade: x.disponibilidade } : {}) }))}
            motivos={motivos}
          />
        ) : null,
    },
  ];

  return (
    <ListView
      titulo="Pessoas"
      icone={<Users size={16} />}
      colunas={colunas}
      linhas={[...linhas]}
      chave={(a) => a.atendenteId}
      vistas={vistas}
      memoria="hugi:equipe:pessoas"
      busca={{ campos: (a) => [a.nome, ...a.filas.map((f) => f.nome)], placeholder: "Buscar pessoa ou time" }}
      selecao={{ selecionada: props.pedida, aoSelecionar: (a) => props.aoAbrir(a.atendenteId) }}
      abrir={{ href: (a) => `/equipe?aba=pessoas&pessoa=${encodeURIComponent(a.atendenteId)}`, aoAbrir: (a) => props.aoAbrir(a.atendenteId) }}
      contagem={(n) => (n === 1 ? "1 pessoa" : `${n} pessoas`)}
      limpar={{ ativo: time !== "", aoLimpar: () => setTime("") }}
      acoes={
        filasDoFiltro.length > 1 ? (
          <select value={time} onChange={(e) => setTime(e.target.value)} aria-label="Filtrar por time" className="hugi-equipe-filtro">
            <option value="">Todos os times</option>
            {filasDoFiltro.map(([id, nome]) => (
              <option key={id} value={id}>
                {nome}
              </option>
            ))}
          </select>
        ) : undefined
      }
      vazio={{ semNada: "Ninguém atendendo ainda. Convide alguém: a pessoa aparece aqui depois de aceitar o convite." }}
    />
  );
}

/** O registro da pessoa, na gaveta: quem é, como está, times, desempenho, jornada e as ações. */
export function RegistroDaPessoa(props: {
  readonly atendente: AtendenteNaLista;
  readonly dados: DadosDaEquipe;
  readonly times: readonly TimeNaLista[];
}) {
  const { atendente: a, dados: d } = props;
  const sessao = useSessao();
  const cliente = useQueryClient();
  const [modo, setModo] = useState<"ver" | "ajustar" | "presenca">("ver");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const administra = sessao.permissoes.includes("manage_team");
  const souEu = a.atendenteId === sessao.usuario.id;
  const supervisiona = supervisionaQuem(sessao.usuario.id, administra, props.times)(a.atendenteId);
  const acesso = d.acessos.find((x) => x.atendenteId === a.atendenteId) ?? null;
  const jornada = estadoDaJornada(a, d.horarioDoTenant, d.fuso);

  const agir = async (acao: "desativar" | "reativar" | "liberar-hoje" | "tirar-restricao") => {
    setOcupado(true);
    setErro(null);
    setAviso(null);
    const r =
      acao === "tirar-restricao"
        ? await operarEquipe(cliente, a.atendenteId, "salvar", { restritoAsFilas: false })
        : await operarEquipe(cliente, a.atendenteId, acao);
    setOcupado(false);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível concluir.");
      return;
    }
    if (acao === "desativar") {
      const n = Number(r.dados?.["devolvidos"] ?? 0);
      setAviso(n === 0 ? "Desativado. Não havia atendimento aberto." : `Desativado, e ${n} atendimento(s) voltou para a fila.`);
    }
    if (acao === "liberar-hoje") setAviso("Liberado até o fim do dia. Vence sozinho.");
    if (acao === "tirar-restricao") setAviso("Agora o que ela vê sai dos times de que é responsável.");
  };

  const trocarPerfil = async (perfil: string) => {
    setOcupado(true);
    setErro(null);
    const r = await trocarPerfilDeAcesso(cliente, a.atendenteId, perfil);
    setOcupado(false);
    if (!r.ok) setErro(r.falha?.erro.mensagem ?? "Não foi possível mudar o perfil.");
    else setAviso(r.aviso ?? "Perfil atualizado.");
  };

  if (modo === "ajustar") return <AjustesDaPessoa atendente={a} aoFechar={() => setModo("ver")} />;
  if (modo === "presenca") {
    return (
      <FormDePresenca
        alvoId={a.atendenteId}
        inicial={a.disponibilidade?.estado === "disponivel" ? "pausa" : "disponivel"}
        motivos={d.motivosDePausa ?? []}
        aoConcluir={(texto) => {
          setAviso(texto);
          setModo("ver");
        }}
      />
    );
  }

  const perfil: ReactNode =
    acesso && d.perfilEhSalvavel && acesso.editavel && administra && !souEu ? (
      <select
        value={acesso.perfil ?? ""}
        disabled={ocupado}
        onChange={(e) => void trocarPerfil(e.target.value)}
        aria-label={`Perfil de acesso de ${a.nome ?? a.atendenteId}`}
        className="hugi-equipe-perfil-seletor"
      >
        {d.perfis.map((p) => (
          <option key={p.id} value={p.id}>
            {p.rotulo}
          </option>
        ))}
      </select>
    ) : acesso?.perfil ? (
      (d.perfis.find((p) => p.id === acesso.perfil)?.rotulo ?? acesso.perfil)
    ) : (
      (acesso?.motivo ?? "—")
    );

  return (
    <div className="hugi-equipe-registro">
      <header className="hugi-equipe-registro-topo">
        <span className="hugi-time-avatar">
          <Retrato nome={a.nome ?? "?"} grande />
          <PontoNaTela naTela={a.naTela} />
        </span>
        {/* O nome já é o título da gaveta: aqui, como ela está agora. */}
        <SeloDePresenca disponibilidade={a.disponibilidade} />
        {souEu ? <Selo tom="neutro">você</Selo> : null}
      </header>

      <dl className="hugi-equipe-campos">
        <Campo rotulo="Perfil">{perfil}</Campo>
        <Campo rotulo="Jornada">
          {JORNADA[jornada]} · {a.jornada ? "própria" : "a da empresa"}
        </Campo>
        <Campo rotulo="Limite">{a.limiteSimultaneo ? `${a.limiteSimultaneo} conversas` : "sem limite"}</Campo>
        <Campo rotulo="Abertas agora">{a.atendimentosAbertos}</Campo>
        <Campo rotulo="Encerradas em 30 dias">{a.finalizados30d}</Campo>
        <Campo rotulo="Tempo médio (30 dias)">
          {a.tempoMedio30dSegundos === null || a.tempoMedio30dSegundos === undefined ? "—" : duracaoCurta(a.tempoMedio30dSegundos)}
        </Campo>
      </dl>

      <section className="hugi-equipe-grupo">
        <h4>Times</h4>
        <FilasDaPessoa atendente={a} />
      </section>

      {a.restritoAsFilas ? (
        <p className="hugi-equipe-atencao">
          Vê só as próprias filas (ajuste antigo)
          <Dica texto="A chave antiga 'Ver só as filas dela'. Hoje o que um supervisor vê sai dos times de que ele é responsável." />
          {administra ? (
            <button type="button" className="v2-link" disabled={ocupado} onClick={() => void agir("tirar-restricao")}>
              desligar
            </button>
          ) : null}
        </p>
      ) : null}

      {!a.ativo && a.desativadoEm ? (
        <p className="hugi-equipe-fraco">Desativado em {new Date(a.desativadoEm).toLocaleString("pt-BR")}.</p>
      ) : null}
      {aviso ? <p role="status" className="hugi-equipe-ok">{aviso}</p> : null}
      {erro ? <p role="alert" className="hugi-equipe-erro">{erro}</p> : null}

      <div className="hugi-equipe-acoes">
        {a.ativo && souEu ? (
          <Botao tipo="secundario" onClick={() => setModo("presenca")} desabilitado={ocupado}>
            Presença
          </Botao>
        ) : null}
        {/* Para quem supervisiona: Pôr em pausa/offline e Redistribuir as conversas dela, no mesmo menu da lista. */}
        {a.ativo && !souEu && supervisiona ? (
          <MenuDoSupervisor
            alvo={{ atendenteId: a.atendenteId, nome: a.nome, abertos: a.atendimentosAbertos, ...(a.disponibilidade ? { disponibilidade: a.disponibilidade } : {}) }}
            podeRedistribuir={sessao.permissoes.includes("reassign_conversation")}
            destinos={d.itens.filter((x) => x.ativo).map((x) => ({ id: x.atendenteId, nome: x.nome, ...(x.disponibilidade ? { disponibilidade: x.disponibilidade } : {}) }))}
            motivos={d.motivosDePausa ?? []}
          />
        ) : null}
        {administra && a.ativo ? (
          <>
            <Botao tipo="secundario" onClick={() => setModo("ajustar")} desabilitado={ocupado}>
              Ajustar
            </Botao>
            {jornada === "fora" ? (
              <Botao tipo="secundario" onClick={() => void agir("liberar-hoje")} desabilitado={ocupado}>
                Liberar hoje
              </Botao>
            ) : null}
            <Botao
              tipo="perigo"
              onClick={() => void agir("desativar")}
              desabilitado={ocupado || souEu}
              {...(souEu ? { motivoDeDesabilitar: "Você não pode desativar a si mesmo. Peça a outro administrador." } : {})}
            >
              Desativar
            </Botao>
          </>
        ) : null}
        {administra && !a.ativo ? (
          <Botao tipo="secundario" onClick={() => void agir("reativar")} desabilitado={ocupado}>
            Reativar
          </Botao>
        ) : null}
      </div>
    </div>
  );
}

function Campo(props: { readonly rotulo: string; readonly children: ReactNode }) {
  return (
    <div>
      <dt>{props.rotulo}</dt>
      <dd>{props.children}</dd>
    </div>
  );
}

/** O ajuste da pessoa: nome, os dois toggles que só restringem, a jornada e o limite de conversas. */
function AjustesDaPessoa(props: { readonly atendente: AtendenteNaLista; readonly aoFechar: () => void }) {
  const { atendente: a } = props;
  const cliente = useQueryClient();
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [nome, setNome] = useState(a.nome ?? "");
  const [jornada, setJornada] = useState<readonly (string | null)[] | null>(a.jornada);
  const [apagar, setApagar] = useState(a.podeApagarMensagem);
  const [historico, setHistorico] = useState(a.podeVerHistorico);
  const [limite, setLimite] = useState(a.limiteSimultaneo ? String(a.limiteSimultaneo) : "");

  const salvar = async () => {
    setOcupado(true);
    setErro(null);
    /*
      "Ver só as filas dela" NÃO vai mais (ADR-0022): o alcance do supervisor sai dos times de que ele é responsável.
      O servidor mantém o que não veio, então quem já tinha a chave continua com ela até alguém a desligar.
    */
    const r = await operarEquipe(cliente, a.atendenteId, "salvar", {
      nome: nome.trim() || null,
      podeApagarMensagem: apagar,
      podeVerHistorico: historico,
      jornada,
      limiteSimultaneo: limite.trim() === "" ? null : Number(limite),
    });
    setOcupado(false);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível concluir.");
      return;
    }
    props.aoFechar();
  };

  const mudarDia = (i: number, valor: string) => {
    const atual = jornada ?? Array<string | null>(7).fill(null);
    const novo = atual.map((v, j) => (j === i ? (valor.trim() === "" ? null : valor) : v));
    // Todos vazios volta a `null` = segue o horário do tenant. É diferente de "não trabalha nunca".
    setJornada(novo.every((v) => v === null) ? null : novo);
  };

  return (
    <div className="hugi-equipe-form">
      <label className="hugi-equipe-campo">
        <span>
          Nome
          <Dica texto="Guardado aqui só para os relatórios não mostrarem o identificador. O nome de verdade é o do Cert4All." />
        </span>
        <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Como aparece nos relatórios" />
      </label>

      <label className="hugi-equipe-campo">
        <span>
          Limite de conversas ao mesmo tempo
          <Dica texto="No limite, ela não pega conversa nova da fila nem recebe de colega. Vazio = sem limite." />
        </span>
        <input type="number" min={1} max={200} value={limite} onChange={(e) => setLimite(e.target.value)} placeholder="sem limite" aria-label="Limite de conversas ao mesmo tempo" />
      </label>

      <fieldset className="hugi-equipe-grupo">
        <legend>
          O que ela pode fazer numa conversa
          <Dica texto="Estes dois só restringem. Ninguém ganha permissão que o papel dela no Cert4All não dá." />
        </legend>
        <Alternar rotulo="Apagar e editar mensagem já enviada" explicacao="Só onde o canal permite." ligado={apagar} aoMudar={setApagar} />
        <Alternar rotulo="Ver atendimentos já encerrados" explicacao="O histórico fechado." ligado={historico} aoMudar={setHistorico} />
      </fieldset>

      <fieldset className="hugi-equipe-grupo">
        <legend>
          Jornada de trabalho
          <Dica texto="Fora dela, a pessoa fica Offline para receber de colega e a IA pode cobrir. Vazio em todos os dias = segue o horário da empresa." />
        </legend>
        <div className="hugi-equipe-jornada">
          {DIAS.map((dia, i) => (
            <label key={dia}>
              <span>{dia}</span>
              <input value={jornada?.[i] ?? ""} onChange={(e) => mudarDia(i, e.target.value)} placeholder="08:00-18:00" aria-label={`Jornada de ${dia}`} />
            </label>
          ))}
        </div>
        {a.liberadoAte ? (
          <p className="hugi-equipe-atencao">
            {/* O `T00:00` não é enfeite: `new Date("2026-08-13")` é meia-noite UTC, e em São Paulo vira dia 12. */}
            Liberada da jornada até {new Date(`${a.liberadoAte}T00:00:00`).toLocaleDateString("pt-BR")}. Vence sozinho.
          </p>
        ) : null}
      </fieldset>

      {erro ? <p role="alert" className="hugi-equipe-erro">{erro}</p> : null}
      <div className="hugi-equipe-acoes">
        <Botao onClick={() => void salvar()} desabilitado={ocupado}>
          {ocupado ? "Salvando…" : "Salvar"}
        </Botao>
        <Botao tipo="secundario" onClick={props.aoFechar} desabilitado={ocupado}>
          Cancelar
        </Botao>
      </div>
    </div>
  );
}

/**
 * Os times da pessoa — e o controle de pôr/tirar (H69). A permissão é a que a ROTA exige: `manage_catalog`.
 *
 * A ESCALADA (spec 23, defeito 1; ADR-0022): quem é restrito (responsável sem `manage_team`, ou a chave antiga) não se
 * põe em time alheio — o "+ time" some na PRÓPRIA linha; e tirar quem é responsável é com quem administra.
 */
function FilasDaPessoa(props: { readonly atendente: AtendenteNaLista }) {
  const { atendente: a } = props;
  const sessao = useSessao();
  const cliente = useQueryClient();
  const catalogos = useCatalogos();
  const administra = sessao.permissoes.includes("manage_team");
  const podeVincular = sessao.permissoes.includes("manage_catalog");
  const restrito = a.restritoAsFilas || a.filas.some((f) => f.responsavel);
  const souEuRestrito = a.atendenteId === sessao.usuario.id && restrito && !administra;
  const podeAcrescentar = podeVincular && !souEuRestrito;
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const disponiveis = (catalogos.data?.filas ?? []).filter((f) => !a.filas.some((x) => x.id === f.id));

  async function mexer(acao: () => Promise<{ ok: boolean; falha?: FalhaDaApi }>) {
    setOcupado(true);
    setErro(null);
    const r = await acao();
    setOcupado(false);
    if (!r.ok) setErro(r.falha?.erro.mensagem ?? "Não deu para mudar os times. Tente de novo.");
  }

  return (
    <div className="hugi-equipe-filas">
      {a.filas.length === 0 ? (
        <span className="hugi-equipe-atencao">
          {podeAcrescentar ? "Sem time: não vê conversa nova chegar. Escolha um ao lado." : "Sem time: não vê conversa nova chegar."}
        </span>
      ) : (
        a.filas.map((f) => (
          <span key={f.id} className="hugi-time-chip">
            {f.nome}
            {f.responsavel ? <Selo tom="info">responsável</Selo> : null}
            {podeVincular && (!f.responsavel || administra) ? (
              <button
                type="button"
                disabled={ocupado}
                onClick={() => void mexer(() => desvincularDaFila(cliente, f.id, a.atendenteId))}
                aria-label={`Tirar ${a.nome ?? a.atendenteId} da fila ${f.nome}`}
                className="hugi-equipe-x"
              >
                ×
              </button>
            ) : null}
          </span>
        ))
      )}
      {podeAcrescentar && disponiveis.length > 0 ? (
        <select
          value=""
          disabled={ocupado}
          aria-label={`Colocar ${a.nome ?? a.atendenteId} numa fila`}
          onChange={(e) => {
            const id = e.target.value;
            e.target.value = "";
            if (id) void mexer(() => vincularAFila(cliente, id, a.atendenteId));
          }}
        >
          <option value="">+ time</option>
          {disponiveis.map((f) => (
            <option key={f.id} value={f.id}>
              {f.nome}
            </option>
          ))}
        </select>
      ) : null}
      {podeAcrescentar && catalogos.isSuccess && catalogos.data.filas.length === 0 ? (
        <span className="hugi-equipe-fraco">Nenhum time ainda — crie um na aba Times.</span>
      ) : null}
      {erro ? <span role="alert" className="hugi-equipe-erro">{erro}</span> : null}
    </div>
  );
}

function Alternar(props: { readonly rotulo: string; readonly explicacao: string; readonly ligado: boolean; readonly aoMudar: (v: boolean) => void }) {
  return (
    <label className="hugi-equipe-linha">
      <input type="checkbox" checked={props.ligado} onChange={(e) => props.aoMudar(e.target.checked)} />
      <span>
        {props.rotulo}
        <small className="hugi-equipe-fraco"> · {props.explicacao}</small>
      </span>
    </label>
  );
}
